import React from 'react';
import { Button } from './ui/Button';
import { ArrowRight, Target, Zap, Shield, Sparkles, Clock, CreditCard } from 'lucide-react';
interface IntroScreenProps {
  onStart: () => void;
  onPreview: () => void;
}
export function IntroScreen({
  onStart,
  onPreview
}: IntroScreenProps) {
  return <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-id="element-91">
      <div className="mb-12 relative inline-block" data-id="element-92">
        <div className="absolute inset-0 bg-[#E10600] blur-[60px] opacity-20 rounded-full" data-id="element-93"></div>
        <h1 className="relative text-6xl md:text-8xl font-display font-bold text-white mb-6 tracking-tight leading-none" data-id="element-94">
          UNLOCK YOUR <br data-id="element-95" />
          <span className="text-[#E10600]" data-id="element-96">SALES POTENTIAL</span>
        </h1>
      </div>

      <p className="text-xl md:text-2xl text-white mb-6 leading-relaxed max-w-2xl mx-auto font-medium" data-id="element-97">
        Discover your sales superpowers — then learn how to leverage them.
      </p>

      {/* Free Trial Banner */}
      <div className="inline-flex items-center gap-3 bg-[#E10600]/10 border border-[#E10600]/30 rounded-md px-5 py-3 mb-14" data-id="element-98">
        <Sparkles className="w-5 h-5 text-[#E10600] flex-shrink-0" data-id="element-99" />
        <span className="text-sm text-[#F5F5F4] font-medium" data-id="element-100">
          Includes{' '}
          <span className="text-[#E10600] font-bold" data-id="element-101">7-day free access</span> to
          AI-powered coaching — no credit card required
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-16 text-left" data-id="element-102">
        <div className="bg-[#111318] border border-[#2a2f3b] p-6 rounded-md hover:border-[#E10600] transition-colors group" data-id="element-103">
          <div className="text-[#E10600] mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-104">
            <Target size={32} data-id="element-105" />
          </div>
          <h3 className="text-white text-xl mb-2 font-display" data-id="element-106">1. DISCOVER</h3>
          <p className="text-[#6B7280] text-sm" data-id="element-107">
            Uncover your natural strengths in drive, resilience, and influence.
          </p>
        </div>
        <div className="bg-[#111318] border border-[#2a2f3b] p-6 rounded-md hover:border-[#E10600] transition-colors group" data-id="element-108">
          <div className="text-[#E10600] mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-109">
            <Zap size={32} data-id="element-110" />
          </div>
          <h3 className="text-white text-xl mb-2 font-display" data-id="element-111">2. IDENTIFY</h3>
          <p className="text-[#6B7280] text-sm" data-id="element-112">
            Learn your sales archetype and the superpowers that come with it.
          </p>
        </div>
        <div className="bg-[#111318] border border-[#2a2f3b] p-6 rounded-md hover:border-[#E10600] transition-colors group" data-id="element-113">
          <div className="text-[#E10600] mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-114">
            <Shield size={32} data-id="element-115" />
          </div>
          <h3 className="text-white text-xl mb-2 font-display" data-id="element-116">3. LEVERAGE</h3>
          <p className="text-[#6B7280] text-sm" data-id="element-117">
            Get an AI-powered playbook to maximize your unique strengths.
          </p>
        </div>
      </div>

      {/* What's Included */}
      <div className="bg-[#111318] border border-[#2a2f3b] rounded-md p-8 mb-16 text-left max-w-xl mx-auto" data-id="element-118">
        <h3 className="text-xl font-display text-white mb-6 text-center tracking-wide" data-id="element-119">
          YOUR FREE TRIAL INCLUDES
        </h3>
        <div className="space-y-4" data-id="element-120">
          <div className="flex items-start gap-3" data-id="element-121">
            <div className="w-6 h-6 rounded-full bg-[#E10600]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-122">
              <span className="text-[#E10600] text-xs" data-id="element-123">✓</span>
            </div>
            <div data-id="element-124">
              <span className="text-white font-medium" data-id="element-125">
                Full Sales DNA Profile
              </span>
              <span className="text-[#6B7280] text-sm block" data-id="element-126">
                Your archetype, trait scores, and detailed breakdown
              </span>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-127">
            <div className="w-6 h-6 rounded-full bg-[#E10600]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-128">
              <span className="text-[#E10600] text-xs" data-id="element-129">✓</span>
            </div>
            <div data-id="element-130">
              <span className="text-white font-medium" data-id="element-131">AI Sales Coach</span>
              <span className="text-[#6B7280] text-sm block" data-id="element-132">
                Personalized coaching based on your unique profile
              </span>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-133">
            <div className="w-6 h-6 rounded-full bg-[#E10600]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-134">
              <span className="text-[#E10600] text-xs" data-id="element-135">✓</span>
            </div>
            <div data-id="element-136">
              <span className="text-white font-medium" data-id="element-137">
                Custom Development Plan
              </span>
              <span className="text-[#6B7280] text-sm block" data-id="element-138">
                Weekly action items tailored to your growth areas
              </span>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-139">
            <div className="w-6 h-6 rounded-full bg-[#E10600]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-140">
              <span className="text-[#E10600] text-xs" data-id="element-141">✓</span>
            </div>
            <div data-id="element-142">
              <span className="text-white font-medium" data-id="element-143">
                Role-Fit Recommendations
              </span>
              <span className="text-[#6B7280] text-sm block" data-id="element-144">
                Know exactly where you'll thrive and why
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-6 mt-8 pt-6 border-t border-[#2a2f3b]" data-id="element-145">
          <div className="flex items-center gap-2 text-[#6B7280] text-xs uppercase tracking-widest" data-id="element-146">
            <Clock className="w-4 h-4" data-id="element-147" />
            <span data-id="element-148">7 Days Free</span>
          </div>
          <div className="flex items-center gap-2 text-[#6B7280] text-xs uppercase tracking-widest" data-id="element-149">
            <CreditCard className="w-4 h-4" data-id="element-150" />
            <span data-id="element-151">No CC Required</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-6" data-id="element-152">
        <Button onClick={onStart} size="lg" className="group min-w-[280px] text-xl py-6" data-id="element-153">
          DISCOVER YOUR SUPERPOWERS
          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-154" />
        </Button>

        <button onClick={onPreview} className="text-sm text-[#6B7280] hover:text-white transition-colors uppercase tracking-widest font-bold border-b border-transparent hover:border-[#E10600] pb-1" data-id="element-155">
          See example results
        </button>
      </div>
    </div>;
}